import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import SellerCard from "../../components/BuyerDashboard/Requirements/SellerCard";
import AcceptModal from "../../components/BuyerDashboard/Requirements/AcceptModal";
import RemovalModal from "../../components/BuyerDashboard/Requirements/RemovalModal";
import ShortlistRemovalModal from "../../components/BuyerDashboard/Requirements/ShortlistRemovalModal";
import DetailedRFQ from "../../components/BuyerDashboard/Requirements/DetailedRFQ";
import { ApiHelper } from "../../utils/ApiHelper";
import {
  SELLER_REQUIREMENTS_BY_BUYER_RFQ_ID,
  UPDATE_SELLER_REQUIREMENT,
  UPDATE_SELLER_BOOKMARK
} from "../../lib/API_KEYS";

export default function ReviewSellers() {
  const [activeTab, setActiveTab] = useState("sellers");
  const [search, setSearch] = useState("");
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeller, setSelectedSeller] = useState(null);

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRemovalModal, setShowRemovalModal] = useState(false);
  const [showShortlistRemovalModal, setShowShortlistRemovalModal] = useState(false);

  const ulRef = useRef(null);
  const tabRefs = useRef({ sellers: null, shortlisted: null, discussion: null });
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const { id } = useParams();

  // Apply search filter
  const filteredSellers = allSellers.filter((seller) => {
    if (search === "") return true;

    const searchLower = search.toLowerCase();
    return (
      seller.companyName?.toLowerCase().includes(searchLower) ||
      seller.name?.toLowerCase().includes(searchLower) ||
      seller.contactPerson?.toLowerCase().includes(searchLower) ||
      seller.location?.toLowerCase().includes(searchLower) ||
      seller.industry?.toLowerCase().includes(searchLower)
    );
  });

  // ---------------- FETCH SELLERS -----------------
  const fetchSellers = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const statusMap = {
        sellers: "Seller Interested",
        shortlisted: ["Buyer Interested", "NDA Initiated"], // "Buyer shows interest on seller proposal"
        discussion: "NDA Signed",//"Buyer and Seller Signed NDA",
      };

      const response = await new ApiHelper().FetchFromServer(
        SELLER_REQUIREMENTS_BY_BUYER_RFQ_ID[0].replace(":requirementId", id),
        SELLER_REQUIREMENTS_BY_BUYER_RFQ_ID[1],
        { status: statusMap[activeTab] }
      );

      setAllSellers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch sellers:", error);
      setAllSellers([]);
    } finally {
      setLoading(false);
    }
  }, [id, activeTab]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  // ---------------- UPDATE STATUS -----------------
  const updateSellerStatus = async (sellerId, newStatus, rejectionReason, rejectionDetail) => {
    try {
      let payload = { status: newStatus };
      if (rejectionReason) payload = { ...payload, rejectionReason }
      if (rejectionDetail) payload = { ...payload, rejectionDetail }
      await new ApiHelper().FetchFromServer(
        UPDATE_SELLER_REQUIREMENT[0].replace(":requirementId", sellerId),
        UPDATE_SELLER_REQUIREMENT[1],
        undefined,
        payload
      );

      await fetchSellers();
    } catch (err) {
      console.error("Error updating seller:", err);
    }
  };

  const updateBookmark = async (sellerId, bookmark) => {
    try {
      let payload = { bookmark };
      await new ApiHelper().FetchFromServer(
        UPDATE_SELLER_BOOKMARK[0].replace(":requirementId", sellerId),
        UPDATE_SELLER_BOOKMARK[1],
        undefined,
        payload
      );

      await fetchSellers();
    } catch (err) {
      console.error("Error updating seller:", err);
    }
  };

  // ---------------- TAB INDICATOR -----------------
  const recalcIndicator = useCallback(() => {
    const el = tabRefs.current[activeTab];
    if (el && ulRef.current) {
      const ulRect = ulRef.current.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setIndicator({ left: elRect.left - ulRect.left, width: elRect.width });
    }
  }, [activeTab]);

  useEffect(() => {
    recalcIndicator();
    window.addEventListener("resize", recalcIndicator);
    return () => window.removeEventListener("resize", recalcIndicator);
  }, [recalcIndicator]);

  // ---------------- MODAL HANDLERS -----------------
  const openAcceptModal = (seller) => {
    setSelectedSeller(seller);
    setShowAcceptModal(true);
  };

  const handleGoToShortlisted = async () => {
    await updateSellerStatus(selectedSeller._id, "Buyer Interested");
    setShowAcceptModal(false);
    setActiveTab("shortlisted");
  };

  const openRemovalModal = (seller) => {
    setSelectedSeller(seller);
    setShowRemovalModal(true);
  };

  const handleRemove = async (rejectionReason) => {
    await updateSellerStatus(selectedSeller._id, "Buyer Rejected", rejectionReason);
    setShowRemovalModal(false);
  };

  const openShortlistRemovalModal = (seller) => {
    setSelectedSeller(seller);
    setShowShortlistRemovalModal(true);
  };

  const handleShortlistRemove = async (rejectionReason, rejectionDetail) => {
    await updateSellerStatus(selectedSeller._id, "Buyer Rejected", rejectionReason, rejectionDetail);
    setShowShortlistRemovalModal(false);
  };

  const handleInitiateNDA = async (seller) => {
    await updateSellerStatus(seller._id, "NDA Initiated");
    // setActiveTab("discussion");
  };

  const handleBookmark = async (seller, bookmark) => {
    await updateBookmark(seller._id, bookmark);
  }

  const handleCloseModal = () => {
    setShowAcceptModal(false);
    setShowRemovalModal(false);
    setShowShortlistRemovalModal(false);
  };

  return (
    <>
      <DetailedRFQ id={id} />

      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="fw-bold text-dark mb-0">Review Sellers</h4>
          <div className="position-relative" style={{ width: "250px" }}>
            <span
              className="position-absolute top-50 translate-middle-y ms-2"
              style={{ pointerEvents: "none" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="gray"
                className="bi bi-search"
                viewBox="0 0 16 16"
              >
                <path
                  d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85
                    3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242
                    1.106a5 5 0 1 1 0-10
                    5 5 0 0 1 0 10z"
                />
              </svg>
            </span>
            <input
              type="text"
              className="form-control ps-5"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ---- TABS ---- */}
        <ul ref={ulRef} className="nav nav-tabs border-0 position-relative mb-4">
          {["sellers", "shortlisted", "discussion"].map((tab) => (
            <li key={tab} className="nav-item">
              <button
                ref={(el) => (tabRefs.current[tab] = el)}
                className={`nav-link bg-transparent border-0 ${activeTab === tab ? "text-dark fw-bold" : "text-muted"
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "sellers"
                  ? `Sellers`
                  : tab === "shortlisted"
                    ? "Shortlisted"
                    : "In Discussion With"}
              </button>
            </li>
          ))}
          <div
            className="tab-underline"
            style={{ left: indicator.left, width: indicator.width }}
          />
        </ul>

        {/* ---- SELLER LIST ---- */}
        {loading ? (
          <div>Loading sellers...</div>
        ) : filteredSellers.length ? (
          filteredSellers.map((seller) => (
            <SellerCard
              key={seller._id}
              seller={seller}
              activeTab={activeTab}
              onBookmark={handleBookmark}
              onAccept={openAcceptModal}
              onReject={openRemovalModal}
              onRemove={openShortlistRemovalModal}
              onNDA={handleInitiateNDA}
            />
          ))
        ) : allSellers.length ? (
          <div className="text-center text-muted py-3">
            No sellers match your search criteria.
          </div>
        ) : (
          <div>No sellers found.</div>
        )}

        {/* ---- MODALS ---- */}
        <AcceptModal
          show={showAcceptModal}
          seller={selectedSeller}
          onConfirm={handleGoToShortlisted}
          onCancel={handleCloseModal}
        />

        <RemovalModal
          show={showRemovalModal}
          seller={selectedSeller}
          onConfirm={handleRemove}
          onCancel={handleCloseModal}
        />

        <ShortlistRemovalModal
          show={showShortlistRemovalModal}
          seller={selectedSeller}
          onConfirm={handleShortlistRemove}
          onCancel={handleCloseModal}
        />

        <style>
          {`
          .tab-underline {
            position: absolute;
            bottom: 0;
            height: 3px;        
            background-color: #181D4C; 
            border-radius: 2px;
            transition: all 0.3s ease;
          }
        `}
        </style>
      </div>
    </>
  );
}
